<template>
  <v-toolbar
    dark
    app
    clipped-left
    fixed
    color="primary"
    :extended="showSmallOrdersList"
  >
    <toolbar-title />
    <template v-if="showSmallOrdersList" slot="extension">
      <v-dialog v-if="dishes.length > 0" v-model="dishesDialog" width="300" rounded>
        <v-card>
          <v-card-title class="headline pb-0">
            <span v-t="'mainComponent.selectedDishes'" />
          </v-card-title>
          <div class="selected-dishes-list">
            <dish-chip
              v-for="(dish, i) in dishes"
              :key="i"
              mobile
              state=""
              :dish="dish"
              :editable="isDayEditable($route.params.day)"
              @removeSelection="removeSelection"
            />
          </div>
        </v-card>
      </v-dialog>
      <div style="margin-left: auto;">
        <v-btn
          v-if="dishes.length > 0"
          color="secondary"
          round
          @click.native="() => dishesDialog = true"
        >
          <span>{{ dishes.length }}</span>
          <v-icon class="ml-1" dark>fastfood</v-icon>
          <v-icon dark right>arrow_drop_down</v-icon>
        </v-btn>
        <orders-list-button
          v-if="isDayEditable($route.params.day) && activeSelectionsState === 'dirty'"
          :dishes="dishes"
          :editable="isDayEditable($route.params.day)"
          :no-selection-made-yet="noSelectionMadeYet"
          :state="activeSelectionsState"
          @saveSelection="onSelectionSave"
        />
        <confirmed-order-button
          :confirmed-order-price="confirmedOrderPrice"
          :loading="loading > 0"
        />
      </div>
    </template>
    <template v-else-if="showBigOrdersList">
      <orders-list-big
        :dishes="dishes"
        :editable="isDayEditable($route.params.day)"
        :state="activeSelectionsState"
        :no-selection-made-yet="noSelectionMadeYet"
        @removeSelection="removeSelection"
        @saveSelection="onSelectionSave"
      />
      <orders-list-button
        v-if="isDayEditable($route.params.day) && activeSelectionsState !== 'done'"
        ref="ordersListButton"
        class="pt-1"
        :editable="isDayEditable($route.params.day)"
        :dishes="dishes"
        :no-selection-made-yet="noSelectionMadeYet"
        :state="activeSelectionsState"
        @saveSelection="onSelectionSave"
      />
      <confirmed-order-button
        class="pt-1"
        :confirmed-order-price="confirmedOrderPrice"
        :loading="loading > 0"
      />
    </template>
    <div class="d-flex align-center toolbar-right-side-items">
      <v-subheader v-if="$vuetify.breakpoint.mdAndUp" class="pt-1">
        <div>
          <div>{{ currentlyLoggedUser }}</div>
          <div>{{ currentlyLoggedUserCityAndBalance }}</div>
        </div>
      </v-subheader>
      <notifications-button />
      <order-rating-button />
    </div>
  </v-toolbar>
</template>

<script>
import gql from 'graphql-tag';
import TimeService from '@/services/TimeService';
import { mapGetters, mapActions } from 'vuex';
import ErrorHandler from '@/services/ErrorHandler';
import NotificationService from '@/services/NotificationService';
import ToolbarTitle from './ToolbarTitle.vue';
import OrdersListBig from './orders-list/OrdersListBig.vue';
import OrdersListButton from './orders-list/OrdersListButton.vue';
import ConfirmedOrderButton from './orders-list/ConfirmedOrderButton.vue';
import NotificationsButton from './notifications/NotificationsButton.vue';
import OrderRatingButton from './order-rating/OrderRatingButton.vue';
import DishChip from './orders-list/DishChip.vue';

export default {
  name: 'AppToolbar',
  components: {
    'toolbar-title': ToolbarTitle,
    'orders-list-button': OrdersListButton,
    'confirmed-order-button': ConfirmedOrderButton,
    'orders-list-big': OrdersListBig,
    'notifications-button': NotificationsButton,
    'order-rating-button': OrderRatingButton,
    'dish-chip': DishChip,
  },
  data() {
    return {
      userSelections: null,
      day: null,
      userName: '',
      tabs: null,
      loading: 0,
      dishesDialog: false,
      loadedDay: {
        dayQuery: '',
        userSelectionsQuery: '',
      },
    };
  },
  computed: {
    ...mapGetters('dishesStore', [
      'activeSelections',
      'confirmedSelections',
      'activeSelectionsState',
      'categories',
      'selectedCategory',
      'noSelectionMadeYet',
    ]),
    ...mapGetters('drawerStore', ['currentRoute']),
    ...mapGetters('userInfoStore', ['userData', 'cities']),
    dishes() {
      const dishes = this.activeSelections.map(s => ({
        price: this.isFreeOfCharge ? 0 : s.price,
        title: s.dishes.map(d => d.title).join(' + '),
        id: s.id,
        isFreeOfCharge: s.isFreeOfCharge,
      }));

      return dishes;
    },
    confirmedOrderPrice() {
      const price = this
        .confirmedSelections
        .map(s => (this.isFreeOfCharge ? 0 : s.price))
        .reduce((a, b) => a + b, 0);

      return price;
    },
    showSmallOrdersList() {
      return this.$vuetify.breakpoint.smAndDown && this.showOrdersList;
    },
    showBigOrdersList() {
      return this.$vuetify.breakpoint.mdAndUp && this.showOrdersList;
    },
    showOrdersList() {
      return this.currentRoute.isDishesOverview && this.day && this.day.providers.length > 0;
    },
    showUpperNavigation() {
      return this.currentRoute.isDishesOverview
        && this.$vuetify.breakpoint.mdAndUp
        && this.categories.length > 0;
    },
    currentlyLoggedUser() {
      return this.userData.displayName;
    },
    currentlyLoggedUserCityAndBalance() {
      return this.userData.cityId
        ? `${this.cities.find(x => x.id === this.userData.cityId).name}, ${this.$t('usersBalancesContainer.balance')}: ${this.userData.balance.toFixed(2)} €`
        : '';
    },
    isFreeOfCharge() {
      if (!this.day) {
        return false;
      }

      return this.day.isFreeOfCharge;
    },
  },
  watch: {
    'currentRoute.isDishesOverview': function func(newValue) {
      if (!newValue) {
        this.loadedDay = {
          dayQuery: '',
          userSelectionsQuery: '',
        };
        this.resetCategories();
      }
    },
    dishes(newValue) {
      if (newValue.length < 1) {
        this.dishesDialog = false;
      }
    },
  },
  methods: {
    ...mapActions('dishesStore', [
      'removeSelection',
      'saveSelection',
      'reloadSelections',
      'resetSelections',
      'selectCategory',
      'resetCategories',
    ]),
    async onSelectionSave() {
      await this.saveSelection({ dayOfWeek: this.currentRoute.day });
    },
    isDayEditable() {
      const isBefore = TimeService.IsDateInPast(this.day.orderUntil);
      return !isBefore;
    },
  },
  apollo: {
    $loadingKey: 'loading',
    userSelections: {
      fetchPolicy: 'network-only',
      query: gql`query UserSelectionsQuery($dayOfWeek: DayOfWeek!) {
        userSelections(dayOfWeek: $dayOfWeek) {
          id
          price
          isFreeOfCharge
          categoryId
          providerId
          isSecondary
          dishes: userOrderedDishes {
            id
            title,
            isSoup
          }
        }
      }`,
      variables() {
        return {
          dayOfWeek: this.currentRoute.day,
        };
      },
      skip() {
        return !this.currentRoute.isDishesOverview
        || !this.currentRoute.day
        || this.loadedDay.userSelectionsQuery === this.currentRoute.day
        || !this.userData.cityId;
      },
      error(error) {
        this.userSelections = null;
        if (!this.loading) {
          ErrorHandler.handleApolloError(error);
        }
      },
      result(res) {
        if (!res.loading) {
          this.loadedDay.userSelectionsQuery = this.currentRoute.day;
          this.reloadSelections(res.data.userSelections);
          NotificationService.clearNetworkNotification();
        }
      },
    },
    day: {
      fetchPolicy: 'network-only',
      query: gql`query DayQuery($dayOfWeek: DayOfWeek!) {
        day(dayOfWeek: $dayOfWeek) {
          orderUntil
          isFreeOfCharge
          providers {
            id
          }
        }
      }`,
      variables() {
        return {
          dayOfWeek: this.currentRoute.day,
        };
      },
      skip() {
        return !this.currentRoute.isDishesOverview
          || !this.currentRoute.day
          || this.loadedDay.dayQuery === this.currentRoute.day
          || this.userData.cityId === 0;
      },
      error(error) {
        if (!this.loading) {
          ErrorHandler.handleApolloError(error);
        }
      },
      update({ day }) {
        this.loadedDay.dayQuery = this.currentRoute.day;
        return day;
      },
    },
  },
};
</script>
<style scoped>
.toolbar-right-side-items {
  min-width: 250px;
  margin-left: auto;
}

.selected-dishes-list {
  display: flex;
  flex-flow: wrap;
  padding: 8px;
}
</style>
